/**
 * FAQ Section Enhancement
 * 
 * Optional JavaScript enhancements for the FAQ section:
 * - URL hash support for deep linking to specific FAQ items
 * - Smooth scroll to FAQ items
 * - Expand/collapse all functionality
 * - Analytics tracking (optional)
 */

class FAQSection extends HTMLElement {
  constructor() {
    super();
    this.section = this.closest('.faq-section');
    this.accordionItems = this.querySelectorAll('accordion-custom');
  }

  connectedCallback() {
    this.handleURLHash();
    this.setupHashNavigation();
    
    // Optional: Add expand/collapse all buttons if data attribute is present
    if (this.dataset.showControls === 'true') {
      this.addControlButtons();
    }
  }

  /**
   * Handle URL hash on page load to open specific FAQ item
   */
  handleURLHash() {
    const hash = window.location.hash;
    if (!hash) return;

    // Support both #faq-1 and #question-text formats
    const targetId = hash.substring(1);
    const targetItem = this.querySelector(`[id="${targetId}"]`) || 
                       this.querySelector(`accordion-custom:nth-child(${targetId.replace('faq-', '')})`);

    if (targetItem) {
      // Open the accordion item
      const details = targetItem.querySelector('details');
      if (details) {
        details.open = true;
      }

      // Smooth scroll to the item
      setTimeout(() => {
        targetItem.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }

  /**
   * Setup hash navigation for all FAQ items
   */
  setupHashNavigation() {
    this.accordionItems.forEach((item, index) => {
      const details = item.querySelector('details');
      const summary = item.querySelector('summary');
      
      if (!item.id) {
        item.id = `faq-${index + 1}`;
      }

      // Update URL hash when accordion is opened
      details?.addEventListener('toggle', () => {
        if (details.open) {
          // Update URL without scrolling
          history.replaceState(null, null, `#${item.id}`);
          
          // Optional: Track analytics
          this.trackFAQOpen(summary?.textContent?.trim() || `FAQ ${index + 1}`);
        }
      });
    });
  }

  /**
   * Add expand/collapse all control buttons
   */
  addControlButtons() {
    const header = this.section?.querySelector('.faq-section__header');
    if (!header) return;

    const controls = document.createElement('div');
    controls.className = 'faq-section__controls';
    controls.innerHTML = `
      <button type="button" class="faq-control-btn" data-action="expand-all">
        Expand All
      </button>
      <button type="button" class="faq-control-btn" data-action="collapse-all">
        Collapse All
      </button>
    `;

    header.appendChild(controls);

    // Add event listeners
    controls.querySelector('[data-action="expand-all"]')?.addEventListener('click', () => {
      this.expandAll();
    });

    controls.querySelector('[data-action="collapse-all"]')?.addEventListener('click', () => {
      this.collapseAll();
    });
  }

  /**
   * Expand all FAQ items
   */
  expandAll() {
    this.accordionItems.forEach(item => {
      const details = item.querySelector('details');
      if (details) details.open = true;
    });
  }

  /**
   * Collapse all FAQ items
   */
  collapseAll() {
    this.accordionItems.forEach(item => {
      const details = item.querySelector('details');
      if (details) details.open = false;
    });
  }

  /**
   * Track FAQ interactions (optional - integrate with your analytics)
   * @param {string} question - The FAQ question text
   */
  trackFAQOpen(question) {
    // Google Analytics 4 example
    if (typeof gtag !== 'undefined') {
      gtag('event', 'faq_interaction', {
        event_category: 'FAQ',
        event_label: question,
        value: 1
      });
    }

    // Google Analytics Universal example
    if (typeof ga !== 'undefined') {
      ga('send', 'event', 'FAQ', 'open', question);
    }

    // Custom analytics example
    if (typeof window.analytics !== 'undefined') {
      window.analytics.track('FAQ Opened', {
        question: question,
        section: 'FAQ Section'
      });
    }
  }
}

// Register the custom element
if (!customElements.get('faq-section')) {
  customElements.define('faq-section', FAQSection);
}

// Optional: Add keyboard shortcuts
document.addEventListener('keydown', (e) => {
  // Ctrl/Cmd + Shift + E to expand all FAQs on page
  if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'E') {
    document.querySelectorAll('faq-section').forEach(section => {
      section.expandAll?.();
    });
  }
  
  // Ctrl/Cmd + Shift + C to collapse all FAQs on page
  if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'C') {
    document.querySelectorAll('faq-section').forEach(section => {
      section.collapseAll?.();
    });
  }
});